import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Play } from "lucide-react";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { formatShortCertificationDate } from "@/domains/certifications/utils";
import ResourceItem from "@/features/certifications/module/components/ResourceItem";
import VideoPlayerModal from "@/features/certifications/module/components/VideoPlayerModal";
import { useResourceDownload } from "@/features/certifications/module/hooks/useResourceDownload";
import {
  ResourcesCardProps,
  VideoSelection,
} from "@/features/certifications/module/components/types";
import {
  buildRecordingRows,
  buildResourceRows,
} from "@/features/certifications/module/utils";

import "./styles.css";

const PLAY_ICON_SIZE = 14;

const ResourcesCard = ({
  moduleId,
  certificationSlug,
  items,
  itemProgress,
  allItems,
}: ResourcesCardProps) => {
  const { t } = useTranslation();
  const [watching, setWatching] = useState<VideoSelection | null>(null);
  const { handleDownload, isBusy, downloadingId } = useResourceDownload({
    moduleId,
    certificationSlug,
  });
  const rows = buildResourceRows(t, items, itemProgress);
  const recordings = buildRecordingRows(items, allItems);

  return (
    <section className="certification-card module-card module-card-body flex flex-col gap-300">
      <h2 className="certification-heading header-s-medium">
        {t("certification.module.resources.heading")}
      </h2>

      {rows.length === 0 && recordings.length === 0 ? (
        <p className="certification-muted body-s-regular">
          {t("certification.module.resources.empty")}
        </p>
      ) : (
        <ul className="module-resource-list flex flex-col">
          {recordings.map((recording) => {
            const { video } = recording;

            return (
              <li
                key={`recording-${recording.id}`}
                className={`module-resource flex items-center gap-300 ${
                  video ? "" : "module-resource-pending"
                }`}
              >
                <span
                  className="module-file-badge module-file-badge-recording flex flex-none items-center justify-center"
                  aria-hidden
                >
                  <Play size={PLAY_ICON_SIZE} />
                </span>

                <div className="module-resource-text flex flex-col flex-1">
                  <div className="flex flex-wrap items-center gap-200">
                    <span className="certification-heading body-s-medium">
                      {recording.title}
                    </span>

                    <span className="module-recording-chip body-xs-regular">
                      {t("certification.module.resources.recordingChip")}
                    </span>
                  </div>

                  <span className="certification-muted body-xs-regular">
                    {t("certification.module.resources.recordedOn", {
                      date: formatShortCertificationDate(recording.recordedAt),
                      minutes: recording.minutes,
                    })}
                  </span>
                </div>

                {video ? (
                  <BaseButton
                    variant={BUTTON_VARIANTS.TERTIARY}
                    size={BUTTON_SIZES.EXTRA_SMALL}
                    ariaLabel={t("certification.module.resources.watchLabel", {
                      title: recording.title,
                    })}
                    onClick={() => setWatching({ video, number: "" })}
                  >
                    {t("certification.module.resources.watch")}
                  </BaseButton>
                ) : (
                  <span className="certification-muted body-xs-regular">
                    {t("certification.module.resources.recordingSoon")}
                  </span>
                )}
              </li>
            );
          })}

          {rows.map((row) => (
            <ResourceItem
              key={row.id}
              row={row}
              isDownloading={downloadingId === row.id}
              isBusy={isBusy}
              onDownload={handleDownload}
            />
          ))}
        </ul>
      )}

      <VideoPlayerModal
        certificationSlug={certificationSlug}
        selection={watching}
        onClose={() => setWatching(null)}
      />
    </section>
  );
};

export default ResourcesCard;
